import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { RunView, CompositeKey } from '@memberjunction/core';
import { UUIDsEqual } from '@memberjunction/global';
import { Subject } from 'rxjs';
import { mjBizAppsCommonOrganizationEntity, mjBizAppsCommonPersonEntity } from '@mj-biz-apps/common-entities';
import {
  mjBizAppsIssuesIssueEntity,
  mjBizAppsIssuesIssueStatusEntity,
  mjBizAppsIssuesIssueTypeEntity,
} from '@mj-biz-apps/issues-entities';
// Runtime (value) import so the bizapps-issues @RegisterClass decorators actually execute.
// The type imports above are erased by the compiler; without this call the Issue entity
// subclasses are never registered, so RunView returns a generic BaseEntity and the typed
// getters (Title/IssueNumber/Status) come back undefined. Mirrors how mj_generatedentities
// registers its entities at bootstrap. Invoked from LoadTicketQueueComponent() below.
import { LoadGeneratedEntities as LoadIssuesEntities } from '@mj-biz-apps/issues-entities';
import { IzzyMetadataEngine, IzzyTicketSLAStateEntity, TicketState } from 'mj_generatedentities';

/** SLA badge state for a ticket row. `kind` drives the color; `label` is the text shown. */
interface SLABadge {
  kind: 'breached' | 'warning' | 'ontrack' | 'none';
  label: string;
}

/**
 * Display row for the ticket queue — an Issue plus the resolved status/type lookups
 * and a presentation-formatted ticket number.
 */
interface TicketRow {
  issue: mjBizAppsIssuesIssueEntity;
  status: mjBizAppsIssuesIssueStatusEntity | null;
  type: mjBizAppsIssuesIssueTypeEntity | null;
  /** Org-facing label, e.g. "TKT-0042", derived from the issue's IssueNumber sequence. */
  ticketLabel: string;
  /** SLA badge derived from the ticket's TicketSLAState (none when no SLA clock). */
  sla: SLABadge;
  /** True when a linked message sits in ApprovalStatus 'Escalated' — same signal as Messages. */
  escalated: boolean;
}

type QuickFilter = 'all' | 'unassigned' | 'open' | 'resolved' | 'escalated';

/**
 * Ticket Queue (PRD v4.0 §11.2).
 *
 * Presentational queue hosted inside the top-level Ticket Workspace Resource. Lists the
 * organization's tickets (bizapps-issues Issues) with number, subject, status, priority,
 * type and assignment, plus search and quick filters. The host passes the active org via
 * `organizationId`; selecting a row emits `openTicket` so the host can open the ticket
 * detail/timeline view (§11.3).
 *
 * A ticket IS an Issue; this component reads `MJ_BizApps_Issues: Issues` scoped to the
 * organization (via the messages linked through Izzy's ChannelMessage.IssueID, and/or the
 * issue's reporter/app scope). Status/type lookups come from the cached IssueEngine data
 * surfaced here via RunView.
 */
@Component({
  standalone: false,
  selector: 'izzy-ticket-queue',
  templateUrl: './ticket-queue.component.html',
  styleUrls: ['./ticket-queue.component.css'],
})
export class TicketQueueComponent implements OnInit, OnDestroy, OnChanges {
  @Input() organizationId: string | undefined;
  /**
   * Optional initial assignee filter (e.g. from an analytics "Tickets by Assignee" drill-in):
   * a Person ID (AssigneeRecordID) to show only that agent's tickets, or the literal `'unassigned'`
   * for tickets with no assignee. Applied on load; the agent can clear it via the filter controls.
   */
  @Input() assigneeFilter: string | null = null;
  /** Emitted when an agent opens a ticket — host shows the detail/timeline view. */
  @Output() openTicket = new EventEmitter<{ issueId: string }>();

  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;
  protected tickets: TicketRow[] = [];
  protected statuses: mjBizAppsIssuesIssueStatusEntity[] = [];
  protected types: mjBizAppsIssuesIssueTypeEntity[] = [];
  /** Resolved assignee display names, keyed by Person ID (AssigneeRecordID). */
  private assigneeNames = new Map<string, string>();

  protected isLoading = true;
  protected errorMessage = '';
  protected searchQuery = '';
  protected quickFilter: QuickFilter = 'open';
  /**
   * False until the FIRST load has decided which filter applies (Escalated when escalations
   * exist, else Open). The chips render immediately for a stable layout, but none is highlighted
   * until this flips — otherwise "Open" flashes active for a beat before jumping to "Escalated".
   * Never reset after that: refreshes and org switches keep the current highlight while loading.
   */
  protected filterReady = false;
  protected statusFilterId = '';
  protected priorityFilter = '';

  /** Active org members for the "By person…" dropdown — the current user is EXCLUDED ("Mine" covers
   *  them), mirroring the message-approval assignee filter. */
  protected orgTeamMembers: { personId: string; name: string }[] = [];
  /** The logged-in user's Person ID — used by the "Mine" filter (referenced in the template). */
  protected currentPersonId: string | null = null;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      const newOrgId = changes['organizationId'].currentValue;
      if (newOrgId && newOrgId !== this.selectedOrganization?.ID) {
        const org = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations().find(o => o.ID === newOrgId);
        if (org) {
          this.selectedOrganization = org;
          void this.loadTickets();
        }
      }
    }
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      await IzzyMetadataEngine.Instance.Config(false);

      const orgs = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();
      this.selectedOrganization =
        (this.organizationId ? orgs.find(o => o.ID === this.organizationId) : orgs[0]) || null;

      await this.loadOrgTeamMembers();

      // Load the IssueStatus + IssueType lookups (small reference sets).
      const rv = new RunView();
      const [statusRes, typeRes] = await rv.RunViews([
        { EntityName: 'MJ_BizApps_Issues: Issue Status', OrderBy: 'Sequence ASC', ResultType: 'entity_object' },
        { EntityName: 'MJ_BizApps_Issues: Issue Types', OrderBy: 'Name ASC', ResultType: 'entity_object' },
      ]);
      this.statuses = (statusRes?.Success ? statusRes.Results : []) as mjBizAppsIssuesIssueStatusEntity[];
      this.types = (typeRes?.Success ? typeRes.Results : []) as mjBizAppsIssuesIssueTypeEntity[];

      await this.loadTickets();
    } catch (error) {
      console.error('TicketQueue: failed to load data', error);
      this.errorMessage = 'Failed to load tickets. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadTickets(): Promise<void> {
    if (!this.selectedOrganization) {
      this.tickets = [];
      this.filterReady = true;
      return;
    }
    try {
      // Tickets for this org = Issues whose linked ChannelMessages belong to the org's channels.
      // Resolve the org's channel IDs, then find Issues referenced by those messages' IssueID.
      const orgChannelIds = IzzyMetadataEngine.Instance.Channels
        .filter(c => c.OrganizationID === this.selectedOrganization!.ID)
        .map(c => c.ID);

      if (orgChannelIds.length === 0) {
        this.tickets = [];
        return;
      }

      const rv = new RunView();
      const channelList = orgChannelIds.map(id => `'${id}'`).join(', ');
      // ApprovalStatus rides along so escalated tickets can be flagged without a second query:
      // a ticket is escalated when any of its linked messages is (same signal as Messages).
      const linkedMsgRes = await rv.RunView({
        EntityName: 'Channel Messages',
        ExtraFilter: `ChannelID IN (${channelList}) AND IssueID IS NOT NULL`,
        Fields: ['IssueID', 'ApprovalStatus'],
      });
      const linkedMsgs = (linkedMsgRes.Success ? linkedMsgRes.Results : []) as Array<{ IssueID: string; ApprovalStatus: string | null }>;
      const issueIds = Array.from(new Set(linkedMsgs.map(r => r.IssueID).filter(Boolean)));
      const escalatedIssueIds = new Set(
        linkedMsgs.filter(r => TicketState.IsEscalatedMessage(r)).map(r => r.IssueID.toLowerCase())
      );

      if (issueIds.length === 0) {
        this.tickets = [];
        return;
      }

      const issueList = issueIds.map(id => `'${id}'`).join(', ');
      const issueRes = await rv.RunView<mjBizAppsIssuesIssueEntity>({
        EntityName: 'MJ_BizApps_Issues: Issues',
        ExtraFilter: `ID IN (${issueList})`,
        OrderBy: '__mj_CreatedAt DESC',
        ResultType: 'entity_object',
      });

      const issues = issueRes.Success ? issueRes.Results : [];

      // SLA states for these tickets (1:1 by IssueID), to derive the SLA badge per row.
      const slaByIssue = new Map<string, IzzyTicketSLAStateEntity>();
      const slaRes = await rv.RunView<IzzyTicketSLAStateEntity>({
        EntityName: 'Ticket SLA States',
        ExtraFilter: `IssueID IN (${issueList})`,
        ResultType: 'entity_object',
      });
      for (const st of slaRes.Success ? slaRes.Results : []) {
        slaByIssue.set(st.IssueID.toLowerCase(), st);
      }

      // Per-org ticket numbers (PRD §5.2), batched over the same issue set. The real org-facing
      // number ({PREFIX}-####, per-org) lives here; we fall back to the legacy faked label only for
      // tickets created before per-org numbering (no IssueTicketNumber row yet).
      const ticketNumByIssue = new Map<string, string>();
      const tnRes = await rv.RunView<{ IssueID: string; TicketNumber: string }>({
        EntityName: 'Issue Ticket Numbers',
        ExtraFilter: `IssueID IN (${issueList})`,
        Fields: ['IssueID', 'TicketNumber'],
      });
      for (const r of tnRes.Success ? tnRes.Results : []) {
        if (r.IssueID && r.TicketNumber) ticketNumByIssue.set(r.IssueID.toLowerCase(), r.TicketNumber);
      }

      // Match lookups by ID with UUIDsEqual — raw === fails when the GraphQL-returned
      // StatusID/IssueTypeID casing differs from the lookup entities' (SQL Server returns
      // uppercase UUIDs). UUIDsEqual normalizes case before comparing.
      this.tickets = issues.map(issue => {
        const status = this.statuses.find(s => UUIDsEqual(s.ID, issue.StatusID)) || null;
        return {
          issue,
          status,
          type: this.types.find(t => UUIDsEqual(t.ID, issue.IssueTypeID)) || null,
          ticketLabel: ticketNumByIssue.get(issue.ID.toLowerCase()) || this.formatTicketLabel(issue),
          sla: this.computeSLABadge(slaByIssue.get(issue.ID.toLowerCase()) || null),
          // Resolution supersedes escalation — the shared TicketState rule, same as ticket-detail.
          escalated: !TicketState.IsClosedOut(status, issue.Status)
            && escalatedIssueIds.has(issue.ID.toLowerCase()),
        };
      })
      // Order by the monotonic IssueNumber sequence (newest first). Concurrent batch inserts get
      // near-identical __mj_CreatedAt timestamps that interleave the SQL OrderBy, so we sort on the
      // parsed numeric sequence here for stable, intuitive ordering regardless of timestamp ties.
      .sort((a, b) => this.issueSeq(b.issue) - this.issueSeq(a.issue));

      await this.resolveAssigneeNames();

      // Auto-select the Escalated filter when escalated tickets exist (highest-priority queue),
      // mirroring the Messages inbox. Only while the filter still sits on the default ('open')
      // and no assignee drill-in is active — never yank the agent off a filter they chose.
      if (this.escalatedCount > 0 && this.quickFilter === 'open' && !this.assigneeFilter) {
        this.quickFilter = 'escalated';
      }
      // Inverse: nothing escalated (last one just got handled, or org switch) while the filter is
      // still on Escalated → fall back to Open. The chip is hidden at zero, so staying on it would
      // strand the agent on an invisible filter showing an empty queue.
      if (this.escalatedCount === 0 && this.quickFilter === 'escalated') {
        this.quickFilter = 'open';
      }
    } catch (error) {
      console.error('TicketQueue: failed to load tickets', error);
      this.errorMessage = 'Failed to load tickets.';
    } finally {
      this.filterReady = true;
    }
  }

  /**
   * Org-facing ticket label. The stored IssueNumber is per-AppScope (e.g. "ISS-1"); Izzy
   * presents it as "TKT-####". When a per-org TicketNumber lands (PRD §5.2) this swaps to it.
   */
  /** Numeric sequence parsed from IssueNumber (e.g. "ISS-42" → 42); -1 when absent/unparseable. */
  private issueSeq(issue: mjBizAppsIssuesIssueEntity): number {
    const num = issue.IssueNumber || '';
    const seq = num.includes('-') ? num.split('-').pop() : num;
    const n = parseInt(seq || '', 10);
    return Number.isNaN(n) ? -1 : n;
  }

  private formatTicketLabel(issue: mjBizAppsIssuesIssueEntity): string {
    const num = issue.IssueNumber || '';
    const seq = num.includes('-') ? num.split('-').pop() : num;
    return seq ? `TKT-${seq.padStart(4, '0')}` : 'TKT-?';
  }

  // ========== Filtering ==========

  get filteredTickets(): TicketRow[] {
    const q = this.searchQuery.trim().toLowerCase();
    return this.tickets.filter(t => {
      if (q) {
        const hay = `${t.issue.Title} ${t.ticketLabel} ${t.issue.IssueNumber || ''} ${this.getReporterLabel(t)} ${t.issue.ReporterEmail || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (this.statusFilterId && !UUIDsEqual(t.issue.StatusID, this.statusFilterId)) return false;
      if (this.priorityFilter && t.issue.Priority !== this.priorityFilter) return false;
      // Assignee drill-in (from analytics): 'unassigned' → no assignee; otherwise match the Person ID.
      if (this.assigneeFilter === 'unassigned' && t.issue.AssigneeRecordID) return false;
      if (this.assigneeFilter && this.assigneeFilter !== 'unassigned'
          && !UUIDsEqual(t.issue.AssigneeRecordID || '', this.assigneeFilter)) return false;
      if (this.quickFilter === 'unassigned' && t.issue.AssigneeRecordID) return false;
      if (this.quickFilter === 'open' && !this.isTicketOpen(t)) return false;
      if (this.quickFilter === 'resolved' && (t.status?.Name || t.issue.Status) !== 'Resolved') return false;
      if (this.quickFilter === 'escalated' && !t.escalated) return false;
      return true;
    });
  }

  /** Count for the Escalated filter chip — visible even when another filter is active. */
  get escalatedCount(): number {
    return this.tickets.filter(t => t.escalated).length;
  }

  /** A ticket is "open" unless closed-out — the shared TicketState rule (terminal or Resolved). */
  private isTicketOpen(t: TicketRow): boolean {
    return !TicketState.IsClosedOut(t.status, t.issue.Status);
  }

  setQuickFilter(f: QuickFilter): void {
    this.quickFilter = f;
    // Quick filters and the assignee filter are mutually exclusive (one selector, like the message
    // queue) — picking a quick filter clears any active Mine/by-person filter.
    this.assigneeFilter = null;
  }

  /** True when the "Mine" filter is active (assignee filter = the logged-in user). */
  get isMineActive(): boolean {
    return !!this.currentPersonId && !!this.assigneeFilter
        && UUIDsEqual(this.assigneeFilter, this.currentPersonId);
  }

  /** "Mine" — show only tickets assigned to the logged-in user (mirrors the message queue). */
  setMine(): void {
    this.assigneeFilter = this.currentPersonId;
    // Assignee filter spans all statuses — reset the quick filter so it isn't stacked on Open/Unassigned/etc.
    this.quickFilter = 'all';
  }

  /** "By person…" dropdown — filter to a specific assignee, or clear when the placeholder is chosen. */
  filterByAssignee(personId: string): void {
    this.assigneeFilter = personId || null;
    this.quickFilter = 'all';
  }

  /**
   * Loads the active org members for the "By person…" dropdown, EXCLUDING the current user (the
   * "Mine" button covers them) — mirrors message-approval's assignee filter. Uses the engine's
   * OrganizationContacts cache (relationships loaded) + a People name lookup.
   */
  private async loadOrgTeamMembers(): Promise<void> {
    this.orgTeamMembers = [];
    const orgId = this.selectedOrganization?.ID;
    if (!orgId) {
      return;
    }
    const engine = IzzyMetadataEngine.Instance;
    this.currentPersonId = engine.CurrentContactID || null;

    const members = engine.OrganizationContacts.filter(
      oc => UUIDsEqual(oc.OrganizationID, orgId) && engine.IsActiveMembership(oc)
            && !(this.currentPersonId && UUIDsEqual(oc.PersonID, this.currentPersonId))
    );
    const personIds = Array.from(new Set(members.map(oc => oc.PersonID)));
    if (personIds.length === 0) {
      return;
    }

    const rv = new RunView();
    const res = await rv.RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID IN (${personIds.map(id => `'${id}'`).join(', ')})`,
      ResultType: 'entity_object',
    });
    const nameById = new Map<string, string>();
    for (const p of res.Success ? res.Results : []) {
      nameById.set(
        p.ID,
        p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Member'
      );
    }
    this.orgTeamMembers = personIds
      .map(id => ({ personId: id, name: nameById.get(id) || 'Member' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  // ========== Display helpers ==========

  /** ~30-minute warning lead, mirroring the SLA sweep (SLAService.WARNING_LEAD_MINUTES). */
  private static readonly SLA_WARNING_LEAD_MS = 30 * 60_000;

  /**
   * Derives the SLA badge from a ticket's TicketSLAState: breached (past due), warning (warned or
   * within the lead window), on-track (resolution due in the future), or none (no SLA clock).
   */
  private computeSLABadge(state: IzzyTicketSLAStateEntity | null): SLABadge {
    if (!state) return { kind: 'none', label: '—' };
    if (state.Breached) return { kind: 'breached', label: 'Breached' };

    const due = state.ResolutionDueAt ? new Date(state.ResolutionDueAt).getTime() : null;
    if (due == null) {
      return { kind: 'ontrack', label: 'On track' };
    }
    const remaining = due - Date.now();
    if (remaining <= 0) {
      // Past due but the sweep hasn't flipped Breached yet (within the next ~15s cycle).
      return { kind: 'breached', label: 'Overdue' };
    }
    if (state.WarningFiredAt || remaining <= TicketQueueComponent.SLA_WARNING_LEAD_MS) {
      return { kind: 'warning', label: `Due in ${this.humanizeMs(remaining)}` };
    }
    return { kind: 'ontrack', label: `Due in ${this.humanizeMs(remaining)}` };
  }

  /** Compact "in Xh Ym" style duration from milliseconds. */
  private humanizeMs(ms: number): string {
    const totalMin = Math.max(1, Math.round(ms / 60_000));
    if (totalMin < 60) return `${totalMin}m`;
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    const mins = totalMin % 60;
    if (days > 0) return `${days}d ${hours}h`;
    return mins ? `${hours}h ${mins}m` : `${hours}h`;
  }

  getStatusColor(t: TicketRow): string {
    return t.status?.ColorCode || 'var(--mj-text-muted)';
  }

  getPriorityClass(t: TicketRow): string {
    return `priority-${(t.issue.Priority || 'medium').toLowerCase()}`;
  }

  getAssigneeLabel(t: TicketRow): string {
    const recId = t.issue.AssigneeRecordID;
    if (!recId) return '—';
    return this.assigneeNames.get(recId.toLowerCase()) || 'Assigned';
  }

  /** Reporter (the customer who raised the ticket) — resolved Person name, else their email. */
  getReporterLabel(t: TicketRow): string {
    const pid = t.issue.ReporterPersonID;
    if (pid) {
      const name = this.assigneeNames.get(pid.toLowerCase());
      if (name) return name;
    }
    return t.issue.ReporterEmail || '—';
  }

  /**
   * Resolves display names for the People the queue shows: assignees (Phase 2 assigns the
   * polymorphic Issue.Assignee to a Person) AND reporters — one batched query, ID → name.
   */
  private async resolveAssigneeNames(): Promise<void> {
    const personIds = Array.from(new Set(
      this.tickets
        .flatMap(t => [t.issue.AssigneeRecordID, t.issue.ReporterPersonID])
        .filter((id): id is string => !!id)
    ));
    if (personIds.length === 0) {
      this.assigneeNames.clear();
      return;
    }
    const rv = new RunView();
    const list = personIds.map(id => `'${id}'`).join(', ');
    const res = await rv.RunView<mjBizAppsCommonPersonEntity>({
      EntityName: 'MJ_BizApps_Common: People',
      ExtraFilter: `ID IN (${list})`,
      ResultType: 'entity_object',
    });
    this.assigneeNames.clear();
    for (const p of res.Success ? res.Results : []) {
      const name = p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Assigned';
      this.assigneeNames.set(p.ID.toLowerCase(), name);
    }
  }

  open(t: TicketRow): void {
    this.openTicket.emit({ issueId: t.issue.ID });
  }
}

export function LoadTicketQueueComponent(): void {
  // Force the bizapps-issues entity subclasses to register their @RegisterClass decorators
  // so RunView hydrates typed Issue entities (see the LoadIssuesEntities import note above).
  LoadIssuesEntities();
}
